import { localCalendarDate } from '@lib/datetime'
import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { resolveSelectedDay } from './core/selection'

const storageKey = (tripId: string): string => `itinerary.selectedDay.${tripId}`

const readRemembered = (tripId: string): number | null => {
	try {
		const raw = window.localStorage.getItem(storageKey(tripId))
		return raw !== null && /^\d+$/.test(raw) ? Number(raw) : null
	} catch {
		return null
	}
}

const remember = (tripId: string, dayIndex: number): void => {
	try {
		window.localStorage.setItem(storageKey(tripId), String(dayIndex))
	} catch {
		// Private windows and blocked storage: the pick just isn't kept.
	}
}

/**
 * The selected trip day: `?day=` (1-based) in the URL so a refresh or a shared
 * link lands on the same day, resolved through the core rule when absent, and
 * remembered per trip so coming back outside the trip reopens the last pick.
 * `tripId` null (the Shared trip view) keeps nothing: a visitor lands on the
 * link's day or the rule's, never on one they picked on an earlier visit.
 */
export const useSelectedDay = (
	tripId: string | null,
	trip: { startDate: string | null | undefined; dayCount: number } | undefined,
): { selectedDay: number; selectDay: (dayIndex: number) => void } => {
	const [params, setParams] = useSearchParams()
	const dayParam = params.get('day')
	const dayCount = trip?.dayCount ?? 0
	const startDate = trip?.startDate

	const selectedDay = useMemo(
		() =>
			resolveSelectedDay({
				startDate,
				dayCount,
				dayParam,
				today: localCalendarDate(),
				remembered: tripId === null ? null : readRemembered(tripId),
			}),
		[startDate, dayCount, dayParam, tripId],
	)

	const selectDay = useCallback(
		(dayIndex: number) => {
			if (tripId !== null) remember(tripId, dayIndex)
			setParams(
				prev => {
					const next = new URLSearchParams(prev)
					next.set('day', String(dayIndex + 1))
					return next
				},
				{ replace: true },
			)
		},
		[tripId, setParams],
	)

	return { selectedDay, selectDay }
}
